import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import firmService from '../services/firmService';

const MarketingStrategy = () => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSuperAdminStatus();
  }, []);

  const checkSuperAdminStatus = async () => {
    try {
      const result = await firmService.checkSuperAdmin();
      setIsSuperAdmin(result.is_super_admin);
    } catch (err) {
      console.error('Error checking super admin status:', err);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-lg p-8 mb-8">
        <h1 className="text-3xl font-bold mb-2">Just Practice</h1>
        <p className="text-xl opacity-90 mb-2">Go-to-Market Strategy</p>
        <p className="text-sm opacity-80">A comprehensive marketing plan for launching the modern practice management system built for small law firms</p>
      </div>

      {/* Executive Summary */}
      <Section title="Executive Summary">
        <QuoteBox>
          "Practice management software that finally understands how small law firms actually work."
          <cite className="block mt-2 text-sm opacity-80">— Just Practice Brand Promise</cite>
        </QuoteBox>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
          <StatBox number="418K+" label="Law Firms in the US (2025)" />
          <StatBox number="95%" label="Firms Have 20 or Fewer Lawyers" />
          <StatBox number="$2.6B" label="Practice Management Software Market" />
          <StatBox number="11-13%" label="Annual Market Growth (CAGR)" />
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mt-6 mb-3">The Opportunity</h3>
        <p className="text-gray-700 mb-4">
          Small law firms (1-3 attorneys) are underserved by existing practice management solutions. With 95% of all law firms having 20 or fewer lawyers, and only 27% of solo attorneys having access to adequate software tools (vs 73% of large firms), there's a massive technology gap. Enterprise tools like Filevine are too complex and expensive, while consumer-grade tools lack essential legal features. Just Practice fills this gap with a purpose-built solution that prioritizes the daily workflow of busy attorneys.
        </p>

        <h3 className="text-lg font-semibold text-blue-900 mt-6 mb-3">Key Differentiators</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon="📅" title="Calendar-Centric Design">
            Attorneys live in their calendars. We built the entire system around this reality, not as an afterthought.
          </Card>
          <Card icon="💰" title="Trust Account Compliance">
            Built-in IOLTA compliance with real-time trust balance tracking and automatic alerts.
          </Card>
          <Card icon="🤖" title="AI-Powered Insights">
            Claude AI integration for case analysis, document processing, and intelligent scheduling.
          </Card>
          <Card icon="📱" title="Unified Communications">
            SMS, email, and document signing in one place. No more juggling multiple tools.
          </Card>
        </div>

        <SourcesBox sources={[
          { name: 'IBISWorld', url: 'https://www.ibisworld.com/industry-statistics/number-of-businesses/law-firms-united-states/', description: '418,000+ law firms in US (2025)' },
          { name: 'ABA 2024 Legal Technology Survey', url: 'https://www.americanbar.org/groups/law_practice/resources/tech-report/2024/2024-solo-and-small-firm-techreport/', description: '95% of firms have 20 or fewer lawyers' },
          { name: 'Yahoo Finance / ResearchAndMarkets', url: 'https://finance.yahoo.com/news/legal-practice-management-software-market-094100480.html', description: '$2.57B market size (2024), 11% CAGR' },
          { name: 'Clio 2025 Legal Trends Report', url: 'https://www.clio.com/blog/solo-small-law-firms-highlights-2025-legal-trends/', description: 'Solo and small firm technology trends' },
        ]} />
      </Section>

      {/* Market Analysis */}
      <Section title="Market Analysis">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Target Market Segmentation</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card icon="🎯" title="Primary: Solo Practitioners">
            <p><strong>Size:</strong> 354,000 firms in the US</p>
            <p><strong>Revenue:</strong> $150K - $500K annually</p>
            <p><strong>Pain:</strong> Wearing too many hats, no support staff, need efficiency</p>
            <p><strong>Opportunity:</strong> Highest volume, fastest decision-making</p>
          </Card>
          <Card icon="🎯" title="Secondary: Small Firms (2-3 attorneys)">
            <p><strong>Size:</strong> 95,000 firms in the US</p>
            <p><strong>Revenue:</strong> $500K - $2M annually</p>
            <p><strong>Pain:</strong> Coordination challenges, inconsistent processes</p>
            <p><strong>Opportunity:</strong> Higher ARPU, team collaboration features</p>
          </Card>
          <Card icon="🎯" title="Tertiary: Boutique Firms (4-10 attorneys)">
            <p><strong>Size:</strong> 48,000 firms in the US</p>
            <p><strong>Revenue:</strong> $2M - $10M annually</p>
            <p><strong>Pain:</strong> Outgrowing basic tools, not ready for enterprise</p>
            <p><strong>Opportunity:</strong> Enterprise-adjacent features, premium pricing</p>
          </Card>
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mb-3">Practice Area Focus</h3>
        <p className="text-gray-700 mb-3">Initial launch will prioritize practice areas with highest software adoption rates and clearest workflow patterns:</p>
        <ul className="space-y-2 mb-6">
          <ListItem><strong>Family Law</strong> — High volume, deadline-driven, trust account heavy, strong template fit</ListItem>
          <ListItem><strong>Estate Planning</strong> — Template-driven, trust management, client communication focus</ListItem>
          <ListItem><strong>Business Law</strong> — Multiple projects per client, flat fee billing, document signing</ListItem>
          <ListItem><strong>Immigration</strong> — Complex workflows, deadline tracking, high client communication</ListItem>
          <ListItem><strong>Personal Injury</strong> — Project-based billing, document-intensive (competitive market, lower priority)</ListItem>
        </ul>

        <h3 className="text-lg font-semibold text-blue-900 mb-3">Competitive Landscape</h3>
        <CompetitorTable />
      </Section>

      {/* Brand Positioning */}
      <Section title="Brand Positioning">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Positioning Statement</h3>
        <QuoteBox>
          For small law firms who are frustrated with complex enterprise software or outgrowing basic tools, Just Practice is the practice management platform that works the way attorneys actually work — calendar-first, with built-in trust compliance, AI assistance, and unified client communication.
        </QuoteBox>

        <h3 className="text-lg font-semibold text-blue-900 mt-6 mb-3">Key Messages by Audience</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <Card icon="🎓" title="For Solo Practitioners">
            <p className="font-medium mb-2">"Your practice, simplified. One tool that handles your calendar, clients, cases, billing, and communications — so you can focus on practicing law."</p>
            <p className="text-sm font-semibold mt-3">Proof Points:</p>
            <ul className="text-sm list-disc list-inside">
              <li>Works on any device (mobile-responsive web)</li>
              <li>No IT department needed</li>
              <li>AI handles document analysis</li>
            </ul>
          </Card>
          <Card icon="👥" title="For Small Firms">
            <p className="font-medium mb-2">"Keep your team aligned without enterprise complexity. Dashboards with current data, workload balancing, and trust oversight — built for firms that move fast."</p>
            <p className="text-sm font-semibold mt-3">Proof Points:</p>
            <ul className="text-sm list-disc list-inside">
              <li>Firm-wide visibility in seconds</li>
              <li>Automatic trust compliance</li>
              <li>Smart task assignment</li>
            </ul>
          </Card>
          <Card icon="💼" title="For Managing Partners">
            <p className="font-medium mb-2">"Trust oversight and financial visibility without micromanaging. Know exactly where every dollar is, and which cases need attention."</p>
            <p className="text-sm font-semibold mt-3">Proof Points:</p>
            <ul className="text-sm list-disc list-inside">
              <li>Trust dashboard with alerts</li>
              <li>Case profitability tracking</li>
              <li>Team utilization insights</li>
            </ul>
          </Card>
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mt-6 mb-3">Brand Voice & Tone</h3>
        <ul className="space-y-2">
          <ListItem><strong>Professional but Approachable</strong> — We understand law, but we don't talk down to users</ListItem>
          <ListItem><strong>Direct and Clear</strong> — No jargon, no fluff, no enterprise-speak</ListItem>
          <ListItem><strong>Confident but Humble</strong> — We're proud of our product but always learning</ListItem>
          <ListItem><strong>Empathetic</strong> — We know running a small firm is hard; we're here to help</ListItem>
        </ul>
      </Section>

      {/* Buyer Personas */}
      <Section title="Buyer Personas">
        <div className="grid lg:grid-cols-3 gap-6">
          <PersonaCard
            name="Rachel Torres"
            role="Solo Family Law Attorney"
            avatar="👩‍⚖️"
            demographics="Age: 38 | Location: Suburban Denver | Revenue: $320K/year"
            background="Left BigLaw 5 years ago. Runs her practice from a small office. No paralegal, uses virtual assistant 10 hrs/week. Handles 40-50 active cases."
            painPoints={[
              'Spends 15+ hours/week on admin',
              'Trust accounting gives her anxiety',
              'Misses deadlines occasionally',
              'Client communication scattered'
            ]}
            solutions={[
              'AI-powered document processing',
              'Automatic trust compliance alerts',
              'Calendar-based deadline tracking',
              'Unified SMS/email inbox'
            ]}
            channels="Bar association events, family law Facebook groups, legal podcasts, Google search for 'family law software'"
          />
          <PersonaCard
            name="David Johnson"
            role="Managing Partner, 3-Attorney PI Firm"
            avatar="👨‍💼"
            demographics="Age: 52 | Location: Atlanta Metro | Revenue: $1.8M/year"
            background="Founded firm 12 years ago. Has 2 associate attorneys, 2 paralegals, 1 office manager. Currently using Clio but frustrated with complexity."
            painPoints={[
              "Can't see team workload at a glance",
              "Paying for features he doesn't use",
              'Associates drop balls on cases',
              'Trust oversight requires manual checking'
            ]}
            solutions={[
              'Firm dashboard with workload view',
              'All-inclusive pricing, no add-ons',
              'Case progress with "next up" tasks',
              'Trust overview dashboard'
            ]}
            channels="Trial lawyer conferences, LinkedIn thought leadership, legal tech reviews, referrals from other managing partners"
          />
          <PersonaCard
            name="Jessica Martinez"
            role="Estate Planning Attorney (New Practice)"
            avatar="👩‍💻"
            demographics="Age: 31 | Location: Austin, TX | Revenue: $95K/year (Year 2)"
            background="Started solo practice 2 years ago after working at a boutique firm. Tech-savvy, bootstrapped, growing client base through referrals."
            painPoints={[
              'Using 6+ different tools/apps',
              'Cash flow tight, every dollar matters',
              'Needs to look professional to clients',
              'Wasting time on repetitive tasks'
            ]}
            solutions={[
              'All-in-one platform consolidation',
              'Caseload pricing — pay for cases, not users',
              'Built-in e-signatures (JustSign)',
              'Case templates for estate planning'
            ]}
            channels="Instagram, TikTok (#LawyerTok), legal subreddits, new attorney bar programs, legal tech blogs"
          />
        </div>
      </Section>

      {/* Marketing Channels */}
      <Section title="Marketing Channels & Tactics">
        <div className="bg-green-50 border border-green-500 rounded-lg p-4 mb-6">
          <h4 className="text-green-800 font-semibold mb-2">💰 Bootstrapped Marketing Strategy</h4>
          <p className="text-green-700 text-sm">
            As a bootstrapped company, we prioritize <strong>low-cost, high-impact channels</strong> that build sustainable growth. We'll reinvest a percentage of revenue into marketing as we grow, starting with organic/free channels and adding paid channels once we have proven unit economics.
          </p>
        </div>

        <div className="space-y-4">
          <ChannelCard
            icon="📝"
            title="Content Marketing & SEO (Primary Focus)"
            cost="Time investment + ~$100/mo tools"
            cac="$50-100 (long-term)"
            tactics={[
              'Blog content: "How to manage trust accounts", "Calendar management for attorneys", practice area guides',
              'Comparison pages: "Just Practice vs Clio", "Best software for [practice area]"',
              'Free resources: Templates, checklists, state bar requirement guides (lead magnets)',
              'YouTube tutorials and product demos (free to produce)',
              'Guest posts on Above the Law, Lawyerist, Attorney at Work (free exposure)',
              'Answer questions on Quora, Avvo, legal forums'
            ]}
          />
          <ChannelCard
            icon="🤝"
            title="Partnerships & Referrals (Zero Upfront Cost)"
            cost="Revenue share only"
            cac="$0 upfront, ~15-20% rev share"
            tactics={[
              'Customer referral program (1 month free credit per successful referral)',
              'Legal consultant and coach partnerships (commission-based, pay only on conversion)',
              'Bar association CLE presentations (often free for speakers, builds credibility)',
              'Integration partnerships (LawPay co-marketing opportunities)',
              'Law school career services partnerships for new graduate discounts',
              'Affiliate program for legal bloggers and influencers'
            ]}
          />
          <ChannelCard
            icon="📱"
            title="Organic Social & Community (Free)"
            cost="Time investment only"
            cac="$0 direct cost"
            tactics={[
              'LinkedIn thought leadership from founders (managing partner audience)',
              'Engage authentically in Facebook groups for solo attorneys',
              'Reddit participation in r/LawFirm, r/lawyers, r/LegalTech (no self-promotion spam)',
              'TikTok/Instagram for younger attorneys (#LawyerTok) if time permits',
              'Build email list from content downloads, nurture with valuable content'
            ]}
          />
          <ChannelCard
            icon="🎙️"
            title="PR & Podcast Appearances (Free)"
            cost="Time investment only"
            cac="$0 direct cost"
            tactics={[
              'Pitch founder stories to legal tech blogs and podcasts',
              'Guest on podcasts: Lawyerist, Maximum Lawyer, Legal Toolkit, Lawyer Forward',
              'Submit for "best of" lists and software reviews',
              'HARO (Help a Reporter Out) for legal technology stories',
              'Local business press for "bootstrapped startup" angle'
            ]}
          />
          <ChannelCard
            icon="🎪"
            title="Events (Low-Cost / Selective)"
            cost="$0-2,000/event"
            cac="Varies"
            tactics={[
              'Local bar association lunch-and-learns (often free for presenters)',
              'Virtual demo days and webinars (free to host)',
              'Attend (don\'t exhibit) major conferences for networking',
              'Consider ABA TECHSHOW booth only after proving product-market fit',
              'Host free "office hours" for attorneys considering new software'
            ]}
          />
          <ChannelCard
            icon="🔍"
            title="Paid Advertising (Future - After Revenue)"
            cost="Start at $500-1,000/mo once profitable"
            cac="$150-300"
            tactics={[
              'Phase 1 (After $10K MRR): Test Google Ads on high-intent keywords ("Clio alternative", "law practice management")',
              'Phase 2 (After $25K MRR): Retargeting campaigns for website visitors',
              'Phase 3 (After $50K MRR): Scale what works, add LinkedIn ads for firm decision-makers',
              'Never spend more than 20% of MRR on paid acquisition'
            ]}
          />
        </div>
      </Section>

      {/* Pricing Strategy */}
      <Section title="Pricing Strategy">
        <p className="text-center text-gray-600 mb-6">
          Simple, transparent pricing based on caseload — not users. <strong>Unlimited team members on every plan.</strong> No need to share logins or pay extra for paralegals and staff.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <PricingCard
            title="Starter"
            price="$99"
            period="/month"
            description="Up to 25 active cases"
            features={[
              'Unlimited users & logins',
              '25 active cases',
              'Unlimited closed case archive',
              'Calendar & task management',
              'Trust account tracking',
              'Client communication (SMS/Email)',
              'Document storage (50GB)',
              'Basic reporting',
              'Email support'
            ]}
          />
          <PricingCard
            title="Practice"
            price="$199"
            period="/month"
            description="Up to 75 active cases"
            featured
            features={[
              'Unlimited users & logins',
              '75 active cases',
              'Everything in Starter, plus:',
              'Firm dashboard & analytics',
              'Workload balancing',
              'Trust oversight dashboard',
              'AI case analysis (Claude)',
              'E-signatures (unlimited)',
              'Document storage (200GB)',
              'Priority support'
            ]}
          />
          <PricingCard
            title="Firm"
            price="$349"
            period="/month"
            description="Up to 200 active cases"
            features={[
              'Unlimited users & logins',
              '200 active cases',
              'Everything in Practice, plus:',
              'Advanced AI features',
              'API access',
              'Dedicated account manager',
              'LawPay integration',
              '500GB storage',
              'Phone & chat support'
            ]}
          />
        </div>

        <div className="bg-amber-50 border border-amber-500 rounded-lg p-4 mb-6">
          <h4 className="text-amber-800 font-semibold mb-2">🏢 Enterprise / High-Volume Pricing</h4>
          <p className="text-amber-700 text-sm">
            Firms with 200+ active cases: <strong>Contact us for custom pricing.</strong> We'll work with you to create a plan that scales with your practice without surprise costs.
          </p>
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mb-3">Pricing Psychology & Tactics</h3>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card title="Caseload-Based = Fair Pricing">
            Unlike per-user pricing, firms aren't penalized for adding paralegals, assistants, or associates. Pay for what you use, not who uses it.
          </Card>
          <Card title="14-Day Free Trial">
            No credit card required. Full access to Practice tier features. Guided onboarding with sample data.
          </Card>
          <Card title="Annual Discount">
            20% off for annual prepayment. Reduces churn and improves cash flow. Marketed as "2 months free."
          </Card>
          <Card title="New Lawyer Discount">
            50% off first year for attorneys admitted to bar within 3 years. Builds loyalty, captures lifetime value.
          </Card>
          <Card title="Migration Incentive">
            Free data migration from Clio, MyCase, or PracticePanther. Reduces switching friction.
          </Card>
          <Card title="Grow Without Penalty">
            Cases naturally close and open. Only active cases count against limits. Closed cases archived forever at no extra cost.
          </Card>
        </div>
      </Section>

      {/* Launch Timeline */}
      <Section title="Launch Timeline">
        <div className="space-y-6 relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-0.5 before:bg-blue-200">
          <TimelineItem date="Month 1-2" title="Phase 1: Foundation (Cost: ~$500)">
            Finalize product for launch. Set up basic marketing infrastructure (landing page, email capture, analytics). Recruit 10-15 beta users from personal network. Begin creating SEO content library.
          </TimelineItem>
          <TimelineItem date="Month 3" title="Phase 2: Soft Launch (Cost: ~$0)">
            Launch to beta users. Gather feedback and iterate on UX. Create 2-3 video testimonials. Publish first SEO articles. Reach out to legal podcasts for guest appearances.
          </TimelineItem>
          <TimelineItem date="Month 4-5" title="Phase 3: Public Launch (Cost: ~$1,000)">
            Public launch with organic PR push (founder story, Product Hunt, legal tech blogs). Launch referral program. Begin posting in legal Facebook groups and forums. Target: 25 paying customers.
          </TimelineItem>
          <TimelineItem date="Month 6-8" title="Phase 4: Organic Growth (Cost: ~$2,000)">
            Double down on content that's working. Launch affiliate/partner program. Speak at local bar associations. Consider small Google Ads test ($500/mo) if MRR exceeds $10K. Target: 50 paying customers.
          </TimelineItem>
          <TimelineItem date="Month 9-12" title="Phase 5: Sustainable Scale (Cost: 10-15% of revenue)">
            Reinvest portion of revenue into proven channels. Hire part-time content writer or VA. Expand to second practice area. Consider first conference attendance. Target: 125 paying customers, $300K ARR.
          </TimelineItem>
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mt-8 mb-4">Key Launch Milestones (Bootstrapped)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox number="25" label="Paying Customers by Month 4" />
          <StatBox number="$10K" label="MRR by Month 6" />
          <StatBox number="75" label="Customers by Month 9" />
          <StatBox number="$300K" label="ARR by Month 12" />
        </div>
      </Section>

      {/* Success Metrics */}
      <Section title="Success Metrics & KPIs">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Primary Business Metrics</h3>
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <MetricCard title="Customer Acquisition Cost (CAC)" target="< $150" note="Bootstrapped = organic-heavy" />
          <MetricCard title="Monthly Recurring Revenue (MRR)" target="$10K" note="Target by Month 6" />
          <MetricCard title="Customer Lifetime Value (LTV)" target="> $4,800" note="24+ months retention" />
          <MetricCard title="Monthly Churn Rate" target="< 3%" note="Industry average is 5-7%" />
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mb-4">Marketing Performance Metrics</h3>
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <MetricCard title="Trial-to-Paid Conversion" target="> 25%" note="From free trial to paying customer" />
          <MetricCard title="Website Conversion Rate" target="> 3%" note="Visitor to trial signup" />
          <MetricCard title="Demo Request Rate" target="> 2%" note="Visitor to demo request" />
          <MetricCard title="Net Promoter Score (NPS)" target="> 50" note="Customer satisfaction benchmark" />
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mb-4">Channel-Specific KPIs (Bootstrapped Focus)</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left">Channel</th>
                <th className="px-4 py-3 text-left">Primary Metric</th>
                <th className="px-4 py-3 text-left">Year 1 Target</th>
                <th className="px-4 py-3 text-left">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3">Content/SEO</td>
                <td className="px-4 py-3">Organic Traffic</td>
                <td className="px-4 py-3">2K visitors/month by M12</td>
                <td className="px-4 py-3">Time only</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3">Referrals</td>
                <td className="px-4 py-3">Referral Rate</td>
                <td className="px-4 py-3">&gt; 20% of new customers</td>
                <td className="px-4 py-3">1 month credit/referral</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3">Partnerships</td>
                <td className="px-4 py-3">Partner-Sourced Revenue</td>
                <td className="px-4 py-3">15% of new MRR</td>
                <td className="px-4 py-3">Revenue share only</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3">Podcast/PR</td>
                <td className="px-4 py-3">Appearances</td>
                <td className="px-4 py-3">6+ podcast appearances</td>
                <td className="px-4 py-3">Time only</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3">Google Ads (Phase 2)</td>
                <td className="px-4 py-3">Cost per Trial</td>
                <td className="px-4 py-3">&lt; $75</td>
                <td className="px-4 py-3">After $10K MRR</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-blue-900 mt-8 mb-4">Year 1 Financial Projections (Bootstrapped)</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="Revenue">
            <p><strong>Month 12 MRR:</strong> $25,000</p>
            <p><strong>Year 1 ARR:</strong> $300,000</p>
            <p><strong>Total Year 1 Revenue:</strong> $150,000</p>
          </Card>
          <Card title="Customers">
            <p><strong>Month 12 Customers:</strong> 125</p>
            <p><strong>Average Revenue/Customer:</strong> $200/month</p>
            <p><strong>LTV (projected):</strong> $4,800</p>
          </Card>
          <Card title="Marketing Spend">
            <p><strong>Year 1 Budget:</strong> ~$15,000 (10% of revenue)</p>
            <p><strong>Blended CAC:</strong> $120</p>
            <p><strong>LTV:CAC Ratio:</strong> 40:1</p>
          </Card>
        </div>
        <p className="mt-4 text-gray-500 text-sm italic">
          Note: Bootstrapped projections assume slower growth but higher efficiency. Most early customers will come from organic channels (content, referrals, partnerships) with near-zero acquisition cost. Paid channels added only after proving product-market fit and achieving positive unit economics.
        </p>
      </Section>

      {/* Footer */}
      <div className="bg-blue-900 text-white rounded-lg p-6 mt-8 text-center">
        <p className="font-semibold">Just Practice — Go-to-Market Strategy</p>
        <p className="text-sm opacity-80 mt-1">Document Version 1.2 | January 2026</p>
        <p className="text-xs opacity-60 mt-2">Market data sourced from IBISWorld, ABA Legal Technology Survey, Clio Legal Trends Report, and ResearchAndMarkets.</p>
      </div>
    </div>
  );
};

// Reusable Components

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-blue-800 mb-4 pb-2 border-b-4 border-amber-500 inline-block">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const Card = ({ icon, title, children }) => (
  <div className="bg-white rounded-lg p-5 shadow border border-gray-200 hover:shadow-lg transition-shadow">
    {icon && <div className="text-3xl mb-3">{icon}</div>}
    {title && <h4 className="text-blue-800 font-semibold mb-2">{title}</h4>}
    <div className="text-gray-600 text-sm">{children}</div>
  </div>
);

const StatBox = ({ number, label }) => (
  <div className="bg-gradient-to-br from-blue-800 to-blue-600 text-white p-4 rounded-lg text-center">
    <div className="text-2xl font-bold">{number}</div>
    <div className="text-xs opacity-90">{label}</div>
  </div>
);

const QuoteBox = ({ children }) => (
  <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-lg text-center my-6">
    <blockquote className="text-lg italic">{children}</blockquote>
  </div>
);

const ListItem = ({ children }) => (
  <li className="flex items-start gap-2 text-gray-700">
    <span className="text-blue-600 font-bold">→</span>
    <span>{children}</span>
  </li>
);

const SourcesBox = ({ sources }) => (
  <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mt-6">
    <h4 className="text-blue-800 font-semibold mb-2">📊 Market Data Sources</h4>
    <ul className="text-sm text-blue-900 space-y-1">
      {sources.map((source, i) => (
        <li key={i}>
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{source.name}</a>
          {' — '}{source.description}
        </li>
      ))}
    </ul>
  </div>
);

const PersonaCard = ({ name, role, avatar, demographics, background, painPoints, solutions, channels }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
    <div className="bg-blue-800 text-white p-4 flex items-center gap-3">
      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl">{avatar}</div>
      <div>
        <h4 className="font-semibold">{name}</h4>
        <p className="text-sm opacity-90">{role}</p>
      </div>
    </div>
    <div className="p-4 text-sm">
      <p className="text-gray-600 mb-3">{demographics}</p>
      <h5 className="font-semibold text-blue-800 mb-1">Background</h5>
      <p className="text-gray-600 mb-3">{background}</p>
      <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-3 rounded-r">
        <h5 className="font-semibold text-red-800 mb-1">Pain Points</h5>
        <ul className="text-red-700 text-xs space-y-1">
          {painPoints.map((p, i) => <li key={i}>• {p}</li>)}
        </ul>
      </div>
      <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-3 rounded-r">
        <h5 className="font-semibold text-green-800 mb-1">Just Practice Solutions</h5>
        <ul className="text-green-700 text-xs space-y-1">
          {solutions.map((s, i) => <li key={i}>• {s}</li>)}
        </ul>
      </div>
      <h5 className="font-semibold text-blue-800 mb-1">How to Reach Them</h5>
      <p className="text-gray-600 text-xs">{channels}</p>
    </div>
  </div>
);

const ChannelCard = ({ icon, title, cost, cac, tactics }) => (
  <div className="bg-white rounded-lg p-5 shadow border border-gray-200 flex gap-4">
    <div className="text-3xl">{icon}</div>
    <div className="flex-1">
      <h4 className="text-blue-800 font-semibold mb-1">{title}</h4>
      <p className="text-gray-500 text-sm mb-2"><strong>Cost:</strong> {cost} | <strong>Expected CAC:</strong> {cac}</p>
      <ul className="text-gray-600 text-sm space-y-1">
        {tactics.map((t, i) => <li key={i}>• {t}</li>)}
      </ul>
    </div>
  </div>
);

const PricingCard = ({ title, price, period, description, features, featured }) => (
  <div className={`bg-white rounded-lg p-6 text-center border-2 ${featured ? 'border-blue-600 relative' : 'border-gray-200'}`}>
    {featured && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
        Most Popular
      </div>
    )}
    <h4 className="text-xl font-semibold text-blue-800 mb-1">{title}</h4>
    <div className="text-3xl font-bold text-gray-900">{price}<span className="text-base font-normal text-gray-500">{period}</span></div>
    <p className="text-gray-500 text-sm mb-4">{description}</p>
    <ul className="text-left text-sm space-y-2 border-t pt-4">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="text-green-600 font-bold">✓</span>
          <span className="text-gray-600">{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const TimelineItem = ({ date, title, children }) => (
  <div className="relative pl-10">
    <div className="absolute left-2 top-1 w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow"></div>
    <span className="inline-block bg-amber-500 text-white text-xs px-2 py-1 rounded-full mb-2">{date}</span>
    <h4 className="text-blue-800 font-semibold mb-1">{title}</h4>
    <p className="text-gray-600 text-sm">{children}</p>
  </div>
);

const MetricCard = ({ title, target, note }) => (
  <div className="bg-white border-l-4 border-blue-600 p-4 rounded-r shadow">
    <h5 className="text-blue-800 font-semibold text-sm mb-1">{title}</h5>
    <div className="text-2xl font-bold text-green-600">{target}</div>
    <p className="text-gray-500 text-xs">{note}</p>
  </div>
);

const CompetitorTable = () => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow text-sm">
      <thead className="bg-blue-800 text-white">
        <tr>
          <th className="px-3 py-2 text-left">Feature</th>
          <th className="px-3 py-2 text-left">Just Practice</th>
          <th className="px-3 py-2 text-left">Clio</th>
          <th className="px-3 py-2 text-left">Filevine</th>
          <th className="px-3 py-2 text-left">MyCase</th>
          <th className="px-3 py-2 text-left">PracticePanther</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Calendar-Centric Design</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ Core Architecture</td>
          <td className="px-3 py-2 text-amber-600">◐ Standard</td>
          <td className="px-3 py-2 text-amber-600">◐ Project-Based</td>
          <td className="px-3 py-2 text-amber-600">◐ Standard</td>
          <td className="px-3 py-2 text-amber-600">◐ Standard</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Built-in Trust Accounting</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ IOLTA w/ Dashboard</td>
          <td className="px-3 py-2 text-green-600">✓ Strong</td>
          <td className="px-3 py-2 text-amber-600">◐ Basic</td>
          <td className="px-3 py-2 text-green-600">✓</td>
          <td className="px-3 py-2 text-green-600">✓</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">AI Integration</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ Claude (Case Analysis)</td>
          <td className="px-3 py-2 text-green-600">✓ Clio Duo (Strong)</td>
          <td className="px-3 py-2 text-green-600">✓ Built-in AI</td>
          <td className="px-3 py-2 text-red-600">✗</td>
          <td className="px-3 py-2 text-red-600">✗</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Unified SMS/Email</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ Native (Twilio/SendGrid)</td>
          <td className="px-3 py-2 text-amber-600">◐ Add-on Cost</td>
          <td className="px-3 py-2 text-amber-600">◐ Limited</td>
          <td className="px-3 py-2 text-green-600">✓ Built-in</td>
          <td className="px-3 py-2 text-amber-600">◐ Basic</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">E-Signatures Built-in</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ JustSign</td>
          <td className="px-3 py-2 text-amber-600">◐ Integration</td>
          <td className="px-3 py-2 text-amber-600">◐ Integration</td>
          <td className="px-3 py-2 text-green-600">✓ Built-in</td>
          <td className="px-3 py-2 text-green-600">✓ Built-in</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Workload Balancing</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ AI-Powered</td>
          <td className="px-3 py-2 text-red-600">✗</td>
          <td className="px-3 py-2 text-amber-600">◐ Manual</td>
          <td className="px-3 py-2 text-red-600">✗</td>
          <td className="px-3 py-2 text-red-600">✗</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Integrations</td>
          <td className="px-3 py-2">LawPay, Google Cal</td>
          <td className="px-3 py-2 text-green-600">✓ 200+ Apps</td>
          <td className="px-3 py-2 text-amber-600">◐ Limited/Buggy</td>
          <td className="px-3 py-2 text-amber-600">◐ Via 3rd Party</td>
          <td className="px-3 py-2 text-amber-600">◐ Standard</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Pricing Model</td>
          <td className="px-3 py-2 font-semibold">By Caseload</td>
          <td className="px-3 py-2">Per User</td>
          <td className="px-3 py-2">Per User (Quote)</td>
          <td className="px-3 py-2">Per User</td>
          <td className="px-3 py-2">Per User</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Starting Price</td>
          <td className="px-3 py-2 font-semibold">$99/mo (∞ users)</td>
          <td className="px-3 py-2">$49/user/mo</td>
          <td className="px-3 py-2">Quote Required</td>
          <td className="px-3 py-2">$39/user/mo</td>
          <td className="px-3 py-2">$49/user/mo</td>
        </tr>
        <tr className="hover:bg-gray-50">
          <td className="px-3 py-2">Primary Target</td>
          <td className="px-3 py-2 text-green-600 font-semibold">✓ Small Firms (1-10)</td>
          <td className="px-3 py-2">Small to Mid-Size</td>
          <td className="px-3 py-2">Mid-Size/Litigation</td>
          <td className="px-3 py-2 text-green-600">✓ Small Firms</td>
          <td className="px-3 py-2 text-green-600">✓ Small Firms</td>
        </tr>
      </tbody>
    </table>
    <p className="text-xs text-gray-500 mt-2 italic">
      Note: Competitor data sourced from vendor websites, Capterra, G2, and SelectHub (January 2026). Features and pricing subject to change.
    </p>
  </div>
);

export default MarketingStrategy;
